// Servicio de API para la app PWA
// Basado en api.ts de la app WEB

import axios from 'axios';
import { getApiBaseUrl } from '../config';
import { getToken } from './auth';

// Crear una instancia de Axios con configuración personalizada
const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Exportar la instancia de api para usar en otros componentes
export default api;

// Interceptor para añadir el token de autorización a cada solicitud
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const url = String(config.url || '');
    if (url.includes('/solar/inverter-config') && config.timeout == null) {
      // GET/PUT de config puede hablar con el inversor; el default global de 10s abortaba el guardado (HTTP 499).
      config.timeout = 120000;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para mostrar errores de respuesta y expulsar usuarios deshabilitados
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Si el usuario está deshabilitado, expulsarlo
    if (error.response?.status === 403 && error.response?.data?.disabled === true) {
      // Limpiar token y usuario
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Emitir evento para que App.tsx maneje el logout
      const { eventBus } = await import('./EventBus');
      eventBus.emit('user-disabled');
    }
    // Si el token expiró o es inválido (401), limpiar y emitir evento para logout
    // EXCEPTO para usuarios dispositivo (sesión persistente)
    if (error.response?.status === 401) {
      // Solo hacer logout si no es un error de login (evitar loops)
      const url = error.config?.url || '';
      // Spotify Connect puede devolver 401 por scopes/token Spotify; no es JWT SHC.
      if (url.includes('/sonos/spotify/connect/')) {
        return Promise.reject(error);
      }
      if (!url.includes('/login') && !url.includes('/logout')) {
        // Verificar si es usuario dispositivo antes de hacer logout
        const userStr = localStorage.getItem('user');
        let isDeviceUser = false;
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            isDeviceUser = user?.role === 'device' || user?.role === 'dispositivo';
          } catch (e) {
            // Ignorar error de parseo
          }
        }
        
        // NO hacer logout automático para usuarios dispositivo (sesión persistente)
        if (!isDeviceUser) {
          // Limpiar token y usuario INMEDIATAMENTE para evitar más requests con token inválido
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Limpiar token del WebSocketService real (la única implementación,
          // definida en App.tsx y expuesta como window.WebSocketService)
          try {
            (window as any).WebSocketService?.clearStoredToken?.();
          } catch (e) {
            // Ignorar si no está disponible todavía
          }
          
          // Emitir siempre evento de expiración para forzar re-login y reconexión WS
          const { eventBus } = await import('./EventBus');
          eventBus.emit('token-expired');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Función para obtener habitaciones especiales
export const getHabitacionesEspeciales = async (): Promise<any> => {
  try {
    const response = await api.get('/habitaciones-especiales');
    return response.data;
  } catch (error) {
    console.error('getHabitacionesEspeciales error:', error);
    return {};
  }
};

// Función para asignar habitación a dispositivos (habitacionId null = desasignar / sin asignar)
export const asignarHabitacion = async (dispositivoIds: number[], habitacionId: number | null): Promise<{
  success: boolean;
  message: string;
  dispositivos: Array<{
    id: number;
    nombre: string;
    tipo: string;
    habitacion_id: number | null;
  }>;
}> => {
  try {
    const response = await api.post('/asignar_habitacion', {
      device_ids: dispositivoIds,
      habitacion_id: habitacionId
    });
    return response.data;
  } catch (error) {
    console.error('Error al asignar habitación:', error);
    throw error;
  }
};

// Función para obtener usuarios
export const getUsers = async (): Promise<any[]> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/usuarios`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener usuarios:', error);
    throw error;
  }
};

// Función para obtener usuarios con permisos (optimizado)
export const getUsersWithPermissions = async (): Promise<any[]> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/usuarios/with-permissions`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener usuarios con permisos:', error);
    throw error;
  }
};

// Función para crear usuario
interface CreateUserPayload {
  nombre?: string;
  username?: string;
  email: string;
  password: string;
  rol: string;
  permissions?: number[];
  device_ip?: string;
  softphone_sip_extension?: string;
  softphone_sip_password?: string;
}

export const createUser = async (userData: CreateUserPayload): Promise<any> => {
  try {
    const payload: any = {
      nombre: userData.nombre ?? userData.username,
      username: userData.username ?? userData.nombre,
      email: userData.email,
      password: userData.password,
      rol: userData.rol,
      permissions: userData.permissions ?? [],
    };
    // Incluir device_ip si está presente
    if (userData.device_ip !== undefined) {
      payload.device_ip = userData.device_ip;
    }
    if (userData.softphone_sip_extension !== undefined) {
      payload.softphone_sip_extension = userData.softphone_sip_extension;
    }
    if (userData.softphone_sip_password !== undefined && userData.softphone_sip_password) {
      payload.softphone_sip_password = userData.softphone_sip_password;
    }
    const response = await axios.post(`${getApiBaseUrl()}/usuarios`, payload);
    return response.data;
  } catch (error) {
    console.error('Error al crear usuario:', error);
    throw error;
  }
};

// Función para actualizar usuario
export const updateUser = async (userId: number, userData: any): Promise<any> => {
  try {
    const response = await axios.put(`${getApiBaseUrl()}/usuarios/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar usuario:', error);
    throw error;
  }
};

// Función para eliminar usuario
export const deleteUser = async (userId: number): Promise<any> => {
  try {
    const response = await axios.delete(`${getApiBaseUrl()}/usuarios/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar usuario:', error);
    throw error;
  }
};

// Función para actualizar página de inicio del usuario
export const updateUserHomePage = async (userId: number, homePage: string | null, homeTab?: number | null, homeRoom?: number | null): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${getApiBaseUrl()}/usuarios/${userId}/home-page`,
      { home_page: homePage, home_tab: homeTab ?? null, home_room: homeRoom ?? null },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error al actualizar página de inicio:', error);
    throw error;
  }
};

// === Bottom nav: orden default (admin) + preferencias por usuario ===

export type BottomNavRowsModeApi = 'collapse' | 'always_two';
export type BottomNavRowsPolicyApi = 'user_choice' | 'force_two';
export type BottomNavLayoutModeApi = 'single_row' | 'multi_row';
export type BottomNavSlotApi = string | null;

export type BottomNavAdminConfig = {
  success: boolean;
  order: BottomNavSlotApi[];
  rows_policy: BottomNavRowsPolicyApi;
  layout_mode: BottomNavLayoutModeApi;
  icons_per_row: number;
  min_icons_per_row: number;
  max_icons_per_row: number;
};

export const getBottomNavDefaultOrder = async (): Promise<BottomNavAdminConfig> => {
  const response = await api.get('/bottom-nav-default-order');
  return response.data;
};

export const updateBottomNavDefaultOrder = async (payload: {
  order?: BottomNavSlotApi[];
  rows_policy?: BottomNavRowsPolicyApi;
  layout_mode?: BottomNavLayoutModeApi;
  icons_per_row?: number;
}): Promise<BottomNavAdminConfig> => {
  const response = await api.put('/bottom-nav-default-order', payload);
  return response.data;
};

export const getBottomNavPreferences = async (
  userId: number
): Promise<{
  success: boolean;
  order: BottomNavSlotApi[];
  rows_mode: BottomNavRowsModeApi;
  rows_policy: BottomNavRowsPolicyApi;
  rows_mode_locked: boolean;
  layout_mode: BottomNavLayoutModeApi;
  icons_per_row: number;
  min_icons_per_row: number;
  max_icons_per_row: number;
  default_order: BottomNavSlotApi[];
  user_reorder_allowed: boolean;
}> => {
  const response = await api.get(`/usuarios/${userId}/bottom-nav-preferences`);
  return response.data;
};

export const updateBottomNavPreferences = async (
  userId: number,
  prefs: { order?: BottomNavSlotApi[]; rows_mode?: BottomNavRowsModeApi }
): Promise<{
  success: boolean;
  order: BottomNavSlotApi[];
  rows_mode: BottomNavRowsModeApi;
  rows_policy?: BottomNavRowsPolicyApi;
  rows_mode_locked?: boolean;
  layout_mode?: BottomNavLayoutModeApi;
  icons_per_row?: number;
  user_reorder_allowed?: boolean;
}> => {
  const response = await api.put(`/usuarios/${userId}/bottom-nav-preferences`, prefs);
  return response.data;
};

// Función para actualizar rol de usuario
export const updateUserRole = async (userId: number, role: string): Promise<any> => {
  try {
    const response = await axios.put(`${getApiBaseUrl()}/usuarios/${userId}/rol`, { role });
    return response.data;
  } catch (error) {
    console.error('Error al actualizar rol de usuario:', error);
    throw error;
  }
};

// Función para habilitar/deshabilitar usuario
export const toggleUserEnabled = async (userId: number): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${getApiBaseUrl()}/usuarios/${userId}/toggle-enabled`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error al habilitar/deshabilitar usuario:', error);
    throw error;
  }
};

// Función para obtener habitaciones
export const getRooms = async (): Promise<any[]> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/habitaciones`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener habitaciones:', error);
    throw error;
  }
};

// Función para obtener permisos de usuario
// Tipo para permisos granulares
export interface RoomPermission {
  room_id: number;
  can_view_statistics: boolean;
  can_view_room: boolean;
  can_control_devices: boolean;
  can_create_automations?: boolean;
}

export const getUserPermissions = async (userId: number): Promise<{ 
  room_ids: number[];  // Para compatibilidad
  permissions?: RoomPermission[];  // Nuevo formato granular
  can_view_cameras: boolean;
  can_use_ai: boolean;
  can_view_music?: boolean;
  can_view_ac?: boolean;
  can_view_tv?: boolean;
  can_view_android_tv?: boolean;
  can_view_heating?: boolean;
  can_view_pileta?: boolean;
  can_view_solar?: boolean;
  can_answer_doorbell?: boolean;
  can_use_phone?: boolean;
  softphone_sip_extension?: string | null;
  has_softphone_sip_password?: boolean;
}> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/get_user_permissions/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener permisos de usuario:', error);
    throw error;
  }
};

// Función para guardar permisos de usuario (formato granular)
export const saveUserPermissions = async (
  userId: number, 
  permissions: RoomPermission[],
  canViewCameras: boolean,
  canUseAI: boolean,
  canViewMusic: boolean,
  canViewAc: boolean = false,
  canViewTv: boolean = false,
  canViewAndroidTv: boolean = false,
  canViewHeating: boolean = false,
  canViewPileta: boolean = false,
  softphone?: {
    can_answer_doorbell?: boolean;
    can_use_phone?: boolean;
    softphone_sip_extension?: string | null;
    softphone_sip_password?: string | null;
  },
  canViewSolar?: boolean,
): Promise<any> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}/save_user_permissions`, {
      user_id: userId,
      permissions: permissions,
      can_view_cameras: canViewCameras,
      can_use_ai: canUseAI,
      can_view_music: canViewMusic,
      can_view_ac: canViewAc,
      can_view_tv: canViewTv,
      can_view_android_tv: canViewAndroidTv,
      can_view_heating: canViewHeating,
      can_view_pileta: canViewPileta,
      ...(canViewSolar !== undefined ? { can_view_solar: canViewSolar } : {}),
      can_answer_doorbell: softphone?.can_answer_doorbell ?? false,
      can_use_phone: softphone?.can_use_phone ?? false,
      softphone_sip_extension: softphone?.softphone_sip_extension ?? null,
      ...(softphone?.softphone_sip_password
        ? { softphone_sip_password: softphone.softphone_sip_password }
        : {}),
    });
    return response.data;
  } catch (error) {
    console.error('Error al guardar permisos de usuario:', error);
    throw error;
  }
};

// Función para iniciar descubrimiento
export const startDiscovery = async (subnets: string[]): Promise<any> => {
  try {
    // IMPORTANTE: el backend bloquea esta respuesta hasta que el script de descubrimiento
    // termina (puede tardar varios minutos). Hay un axios.defaults.timeout = 10000 global, así
    // que SIN este override el POST se abortaba a los 10s (status 0), disparando el catch que
    // cerraba el stream de logs antes de tiempo (log cortado + "Último descubrimiento: N/A").
    // timeout: 0 = sin límite del lado cliente (el servidor/nginx siguen acotando con 300s).
    const response = await axios.post(`${getApiBaseUrl()}/start_discovery`, {
      subredes: subnets
    }, { timeout: 0 });
    return response.data;
  } catch (error) {
    console.error('Error al iniciar descubrimiento:', error);
    throw error;
  }
};

// Función para descubrir dispositivos SHC
export const discoverShcDevices = async (): Promise<any> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}/discovery/shc`);
    return response.data;
  } catch (error) {
    console.error('Error al descubrir dispositivos SHC:', error);
    throw error;
  }
};

// Función para sincronizar dispositivos SHC
export const syncShcDevices = async (): Promise<any> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}/discovery/sync`);
    return response.data;
  } catch (error) {
    console.error('Error al sincronizar dispositivos SHC:', error);
    throw error;
  }
};

// Función para obtener tipos de artefacto (cached)
export const fetchTiposArtefactoCached = async (): Promise<any[]> => {
  try {
    const response = await api.get('/tipos-artefacto');
    return response.data;
  } catch (error) {
    console.error('Error al obtener tipos de artefacto:', error);
    throw error;
  }
};

/**
 * Eliminar múltiples dispositivos
 * @param ids Array de IDs de dispositivos a eliminar
 * @returns Respuesta de la API
 */
export const deleteDispositivos = async (ids: number[]): Promise<any> => {
  try {
    const response = await api.post('/dispositivos/delete', { ids }, { timeout: 20000 });
    return response.data;
  } catch (error) {
    console.error('deleteDispositivos error:', error);
    throw error;
  }
};

// Función para actualizar el orden de dispositivos dentro de una habitación
export const updateOrdenDispositivos = async (dispositivos: { id: number; orden: number }[]): Promise<any> => {
  try {
    const response = await api.put('/dispositivos/orden', dispositivos);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar orden de dispositivos:', error);
    throw error;
  }
};

// Función para borrar el orden personalizado de dispositivos
export const borrarOrdenDispositivos = async (dispositivos: { id: number }[]): Promise<any> => {
  try {
    const response = await api.post('/dispositivos/orden/borrar', dispositivos);
    return response.data;
  } catch (error) {
    console.error('Error al borrar orden de dispositivos:', error);
    throw error;
  }
};

// Función para renombrar un dispositivo
export const renameDispositivo = async (id: number, nombre: string): Promise<any> => {
  try {
    const response = await api.put(`/dispositivos/${id}/renombrar`, { nombre });
    return response.data;
  } catch (error) {
    console.error('Error al renombrar dispositivo:', error);
    throw error;
  }
};

// Función para reemplazar MAC de un dispositivo (mismo ID)
export const replaceDeviceMac = async (deviceId: number, newMac: string): Promise<any> => {
  try {
    const response = await api.post(`/dispositivos/${deviceId}/reemplazar`, {
      new_mac: newMac
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Validación post-reemplazo para confirmar estado del mismo ID
export const validateDeviceReplacement = async (deviceId: number): Promise<any> => {
  try {
    const response = await api.get(`/dispositivos/${deviceId}/reemplazo/validar`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const applyReplacementConfig = async (deviceId: number): Promise<any> => {
  const response = await api.post(`/dispositivos/${deviceId}/reemplazo/aplicar-config`);
  return response.data;
};

export const releaseReplacementConfigFreeze = async (deviceId: number): Promise<any> => {
  const response = await api.post(`/dispositivos/${deviceId}/reemplazo/liberar-foto`);
  return response.data;
};

// ============================================
// TABLEROS
// ============================================

// Función para obtener todos los tableros
export const getTableros = async (): Promise<any[]> => {
  try {
    const response = await api.get('/tableros');
    return response.data;
  } catch (error) {
    console.error('Error al obtener tableros:', error);
    throw error;
  }
};

// Función para crear un nuevo tablero
export const createTablero = async (nombre: string): Promise<any> => {
  try {
    const response = await api.post('/tableros', { nombre });
    return response.data;
  } catch (error) {
    console.error('Error al crear tablero:', error);
    throw error;
  }
};

// Función para renombrar un tablero
export const updateTableroName = async (id: number, nombre: string): Promise<any> => {
  try {
    const response = await api.put(`/tableros/${id}/renombrar`, { nombre });
    return response.data;
  } catch (error) {
    console.error('Error al renombrar tablero:', error);
    throw error;
  }
};

// Función para eliminar un tablero
export const deleteTablero = async (id: number): Promise<any> => {
  try {
    const response = await api.delete(`/tableros/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar tablero:', error);
    throw error;
  }
};

// Función para actualizar el orden de tableros
export const updateOrdenTableros = async (tableros: { id: number; orden: number }[]): Promise<any> => {
  try {
    const response = await api.put('/tableros/orden', tableros);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar orden de tableros:', error);
    throw error;
  }
};

// Función para obtener habitaciones de un tablero (para validación)
export const getHabitacionesByTablero = async (tableroId: number): Promise<any[]> => {
  try {
    const response = await api.get(`/tableros/${tableroId}/habitaciones`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener habitaciones del tablero:', error);
    throw error;
  }
};

// ============================================
// HABITACIONES
// ============================================

// Función para obtener todas las habitaciones
export const getHabitaciones = async (): Promise<any[]> => {
  try {
    const response = await api.get('/habitaciones');
    return response.data;
  } catch (error) {
    console.error('Error al obtener habitaciones:', error);
    throw error;
  }
};

// Función para crear una nueva habitación
export const createHabitacion = async (nombre: string, tableroId: number): Promise<any> => {
  try {
    const response = await api.post('/habitaciones', { nombre, tablero_id: tableroId });
    return response.data;
  } catch (error) {
    console.error('Error al crear habitación:', error);
    throw error;
  }
};

// Función para renombrar una habitación
export const updateHabitacionName = async (id: number, nombre: string): Promise<any> => {
  try {
    const response = await api.put(`/habitaciones/${id}/renombrar`, { nombre });
    return response.data;
  } catch (error) {
    console.error('Error al renombrar habitación:', error);
    throw error;
  }
};

// Función para eliminar una habitación
export const deleteHabitacion = async (id: number): Promise<any> => {
  try {
    const response = await api.delete(`/habitaciones/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar habitación:', error);
    throw error;
  }
};

// Función para actualizar el orden de habitaciones
export const updateOrdenHabitaciones = async (habitaciones: { id: number; orden: number }[]): Promise<any> => {
  try {
    const response = await api.put('/habitaciones/orden', habitaciones);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar orden de habitaciones:', error);
    throw error;
  }
};

// Función para cambiar una habitación de tablero
export const cambiarTableroHabitacion = async (habitacionId: number, tableroId: number): Promise<any> => {
  try {
    const response = await api.put(`/habitaciones/${habitacionId}/cambiar-tablero`, { tablero_id: tableroId });
    return response.data;
  } catch (error) {
    console.error('Error al cambiar tablero de habitación:', error);
    throw error;
  }
};

// Función para obtener dispositivos de una habitación (para validación)
export const getDispositivosByHabitacion = async (habitacionId: number): Promise<any[]> => {
  try {
    const response = await api.get(`/habitaciones/${habitacionId}/dispositivos`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener dispositivos de la habitación:', error);
    throw error;
  }
};

// Función para obtener el intervalo de polling actual
export const getPollingInterval = async (): Promise<any> => {
  try {
    const response = await api.get('/get-polling-interval');
    return response.data;
  } catch (error) {
    console.error('Error al obtener intervalo de polling:', error);
    throw error;
  }
};

// Función para actualizar el intervalo de polling
export const updatePollingInterval = async (interval: number): Promise<any> => {
  try {
    const response = await api.post('/update-polling', { interval });
    return response.data;
  } catch (error) {
    console.error('Error al actualizar intervalo de polling:', error);
    throw error;
  }
};

const MQTT_SHELLY_PASSWORD_MASK = '********';

export const getMqttShellyCredentials = async (): Promise<{
  success: boolean;
  username: string;
  password_configured: boolean;
  password: string;
}> => {
  const response = await api.get('/mqtt-shelly-credentials');
  return response.data;
};

export const updateMqttShellyCredentials = async (
  username: string,
  password: string
): Promise<{
  success: boolean;
  username: string;
  password_configured: boolean;
}> => {
  const response = await api.put('/mqtt-shelly-credentials', {
    username,
    password: password === MQTT_SHELLY_PASSWORD_MASK ? MQTT_SHELLY_PASSWORD_MASK : password,
  });
  return response.data;
};

// === Ciclo de facturación (configuración global del sistema) ===

export type BillingCycleMode = 'calendar' | 'rolling';

export interface BillingCycleConfigResponse {
  success: boolean;
  mode: BillingCycleMode;
  day: number;
  configured: boolean;
  rolling_epoch_date: string | null;
  rolling_length_days: number;
  rolling_configured: boolean;
}

/** Respuesta extendida (compatible con código que sólo leía day/configured). */
export type BillingCycleDayResponse = BillingCycleConfigResponse;

/** Configuración completa: modo calendario o rolling + parámetros. */
export const getBillingCycleConfig = async (): Promise<BillingCycleConfigResponse> => {
  const response = await api.get('/billing-cycle-day');
  return response.data;
};

/** Alias histórico — mismo endpoint que getBillingCycleConfig. */
export const getBillingCycleDay = async (): Promise<BillingCycleDayResponse> => {
  return getBillingCycleConfig();
};

export interface BillingCycleUpdatePayload {
  mode: BillingCycleMode;
  day?: number;
  rolling_epoch_date?: string | null;
  rolling_length_days?: number;
}

/** Guarda modo calendar o rolling (sólo admin). */
export const updateBillingCycleConfig = async (
  payload: BillingCycleUpdatePayload
): Promise<BillingCycleConfigResponse> => {
  const response = await api.put('/billing-cycle-day', payload);
  return response.data;
};

/** Legado: sólo día → fuerza modo calendar en el backend. */
export const updateBillingCycleDay = async (day: number): Promise<BillingCycleConfigResponse> => {
  return updateBillingCycleConfig({ mode: 'calendar', day });
};

/** Factor de corrección (W) del recuadro Consumo del modal Info. Rango -1000..1000. */
export const getConsumptionCorrectionWatts = async (): Promise<{
  success: boolean;
  correction_watts: number;
}> => {
  const response = await api.get('/consumption-correction-watts');
  return response.data;
};

export const updateConsumptionCorrectionWatts = async (
  correctionWatts: number
): Promise<{ success: boolean; correction_watts: number }> => {
  const response = await api.put('/consumption-correction-watts', {
    correction_watts: correctionWatts,
  });
  return response.data;
};

/** Intervalo entre capturas solares (segundos), 5–120. GET/PUT /api/shc-solar-capture-interval */
export const getSolarCaptureInterval = async (): Promise<{ success: boolean; interval_seconds: number }> => {
  const response = await api.get('/shc-solar-capture-interval');
  return response.data;
};

export const updateSolarCaptureInterval = async (
  intervalSeconds: number
): Promise<{ success: boolean; interval_seconds: number }> => {
  const response = await api.put('/shc-solar-capture-interval', { interval_seconds: intervalSeconds });
  return response.data;
};

/** Intervalo de muestreo histórico Samsung (actividad/temperatura), segundos. */
export const getSamsungHistorySampleInterval = async (): Promise<{
  success: boolean;
  interval_seconds: number;
}> => {
  const response = await api.get('/samsung-history-sample-interval');
  return response.data;
};

export const updateSamsungHistorySampleInterval = async (
  intervalSeconds: number
): Promise<{ success: boolean; interval_seconds: number }> => {
  const response = await api.put('/samsung-history-sample-interval', {
    interval_seconds: intervalSeconds,
  });
  return response.data;
};

/** Intervalo de muestreo histórico evoHome (temp/setpoint/demanda), segundos. */
export const getEvohomeHistorySampleInterval = async (): Promise<{
  success: boolean;
  interval_seconds: number;
}> => {
  const response = await api.get('/evohome-history-sample-interval');
  return response.data;
};

export const updateEvohomeHistorySampleInterval = async (
  intervalSeconds: number
): Promise<{ success: boolean; interval_seconds: number }> => {
  const response = await api.put('/evohome-history-sample-interval', {
    interval_seconds: intervalSeconds,
  });
  return response.data;
};

/** Hora local del backup diario de BD (pgBackRest; full los domingos). */
export const getBackupSchedule = async (): Promise<{
  success: boolean;
  hour: number;
  on_calendar?: string;
  next_elapse?: string | null;
}> => {
  const response = await api.get('/backup-schedule');
  return response.data;
};

export const updateBackupSchedule = async (
  hour: number
): Promise<{
  success: boolean;
  hour: number;
  on_calendar?: string;
  next_elapse?: string | null;
  error?: string;
}> => {
  const response = await api.put('/backup-schedule', { hour });
  return response.data;
};

export interface BackupStatusBackup {
  label: string;
  type: 'full' | 'diff' | 'incr';
  start: number;
  stop: number;
  db_bytes: number;
  delta_bytes: number;
  error?: boolean;
}

/** Estado del backup (status.json de shc_db_backup_check.sh + timers). */
export interface BackupStatus {
  success: boolean;
  available: boolean;
  ok: boolean;
  alerts: string[];
  generated_at?: string;
  backups?: BackupStatusBackup[];
  last_backup?: BackupStatusBackup | null;
  last_full?: BackupStatusBackup | null;
  last_backup_age_hours?: number;
  repo_bytes?: number;
  disk_free_bytes?: number;
  pgdata_bytes?: number;
  archiver?: {
    archive_mode: string;
    archived_count: number;
    last_archived_wal: string;
    last_archived_at: number;
    failed_count: number;
    last_failed_at: number;
    broken: boolean;
  };
  verify?: { at: string; ok: boolean; duration_s: number } | null;
  restore_test?: { at: string; ok: boolean; backup_label: string; duration_s: number; message: string } | null;
  timers?: Record<string, string | null>;
}

export const getBackupStatus = async (): Promise<BackupStatus> => {
  const response = await api.get('/backup-status');
  return response.data;
};

/** Máximo de mensajes favoritos del Asistente por usuario (admin configurable). */
export const getAssistantMessageFavoritesMax = async (): Promise<{
  success: boolean;
  max_favorites: number;
}> => {
  const response = await api.get('/assistant-message-favorites-max');
  return response.data;
};

export const updateAssistantMessageFavoritesMax = async (
  maxFavorites: number
): Promise<{ success: boolean; max_favorites: number }> => {
  const response = await api.put('/assistant-message-favorites-max', {
    max_favorites: maxFavorites,
  });
  return response.data;
};

/** Tier 2 (LLM) del Asistente — solo admin puede cambiarlo. */
export const getAssistantLlmRouting = async (): Promise<{
  success: boolean;
  llm_routing_enabled: boolean;
  llm_host_ip?: string;
}> => {
  const response = await api.get('/assistant-llm-routing');
  return response.data;
};

export const updateAssistantLlmRouting = async (
  enabled: boolean
): Promise<{ success: boolean; llm_routing_enabled: boolean }> => {
  const response = await api.put('/assistant-llm-routing', {
    llm_routing_enabled: enabled,
  });
  return response.data;
};

export const updateAssistantLlmHost = async (
  ip: string
): Promise<{ success: boolean; llm_host_ip: string }> => {
  const response = await api.put('/assistant-llm-host', {
    llm_host_ip: ip,
  });
  return response.data;
};

// === Funciones para autenticación de dispositivos ===

// Función para obtener la información de autenticación de un dispositivo
export const getDeviceAuth = async (deviceId: number): Promise<any> => {
  try {
    const response = await api.get(`/devices/${deviceId}/auth`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener autenticación del dispositivo:', error);
    throw error;
  }
};

// Función para actualizar las credenciales de autenticación de un dispositivo
export const updateDeviceAuth = async (
  deviceId: number,
  credentials: {
    auth_username?: string;
    auth_password?: string;
    auth_pin?: string;
  }
): Promise<any> => {
  try {
    const response = await api.put(`/devices/${deviceId}/auth`, credentials);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar autenticación del dispositivo:', error);
    throw error;
  }
};

// ============================================
// FAVORITOS
// ============================================

// Función para obtener los favoritos de un usuario
// Función para obtener favoritos del usuario (con caché)
export const getUserFavorites = async (userId: number, forceRefresh: boolean = false): Promise<any[]> => {
  try {
    // Si no se fuerza refresh, intentar obtener desde el mapa primero
    if (!forceRefresh) {
      const { mapaFavoritosDispositivos } = await import('./consumptionService');
      const favoritosDelMapa = Object.values(mapaFavoritosDispositivos[userId] || {});
      if (favoritosDelMapa.length > 0) {
        // Retornar del mapa pero también hacer refresh en background
        refreshFavorites(userId).catch(() => {});
        return favoritosDelMapa;
      }
    }
    
    // Si no hay en el mapa o se fuerza refresh, consultar al backend
    const response = await api.get(`/usuarios/${userId}/favoritos`);
    const favoritos = response.data;
    
    // Actualizar mapa
    await updateFavoritesMap(userId, favoritos);
    
    return favoritos;
  } catch (error) {
    console.error('Error al obtener favoritos:', error);
    throw error;
  }
};

// Función helper para actualizar el mapa de favoritos
export const updateFavoritesMap = async (userId: number, favoritos: any[]): Promise<void> => {
  const { mapaFavoritosDispositivos } = await import('./consumptionService');
  
  if (!mapaFavoritosDispositivos[userId]) {
    mapaFavoritosDispositivos[userId] = {};
  }
  
  favoritos.forEach((fav) => {
    if (fav.device_id) {
      mapaFavoritosDispositivos[userId][fav.device_id] = fav;
    }
  });
};

// Función helper para refrescar favoritos del usuario (sin retornar, solo actualiza mapa)
const refreshFavorites = async (userId: number): Promise<void> => {
  try {
    const response = await api.get(`/usuarios/${userId}/favoritos`);
    await updateFavoritesMap(userId, response.data);
  } catch (error) {
    // Silencioso, solo actualiza mapa
  }
};

// Función para agregar un dispositivo a favoritos
export const addFavorite = async (userId: number, deviceId: number): Promise<any> => {
  try {
    const response = await api.post(`/usuarios/${userId}/favoritos`, { device_id: deviceId });
    
    // Actualizar mapa de favoritos
    const { mapaFavoritosDispositivos } = await import('./consumptionService');
    if (!mapaFavoritosDispositivos[userId]) {
      mapaFavoritosDispositivos[userId] = {};
    }
    
    // Obtener el favorito completo del backend para actualizar el mapa
    const favoritesResponse = await api.get(`/usuarios/${userId}/favoritos`);
    await updateFavoritesMap(userId, favoritesResponse.data);
    
    return response.data;
  } catch (error) {
    console.error('Error al agregar favorito:', error);
    throw error;
  }
};

// Función para eliminar un dispositivo de favoritos
export const removeFavorite = async (userId: number, deviceId: number): Promise<any> => {
  try {
    const response = await api.delete(`/usuarios/${userId}/favoritos/${deviceId}`);
    
    // Eliminar del mapa
    const { mapaFavoritosDispositivos } = await import('./consumptionService');
    if (mapaFavoritosDispositivos[userId] && mapaFavoritosDispositivos[userId][deviceId]) {
      delete mapaFavoritosDispositivos[userId][deviceId];
    }
    
    // Actualizar is_favorite en mapaDispositivos
    const { mapaDispositivos } = await import('./consumptionService');
    if (mapaDispositivos[deviceId]) {
      mapaDispositivos[deviceId].is_favorite = false;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error al eliminar favorito:', error);
    throw error;
  }
};

// Función para actualizar el orden de favoritos
export const updateFavoritesOrder = async (userId: number, orderData: { device_id: number; orden: number }[]): Promise<any> => {
  try {
    const response = await api.put(`/usuarios/${userId}/favoritos/orden`, orderData);
    
    // Actualizar orden en el mapa
    const { mapaFavoritosDispositivos } = await import('./consumptionService');
    if (mapaFavoritosDispositivos[userId]) {
      orderData.forEach(({ device_id, orden }) => {
        if (mapaFavoritosDispositivos[userId][device_id]) {
          mapaFavoritosDispositivos[userId][device_id].orden = orden;
        }
      });
    }
    
    return response.data;
  } catch (error) {
    console.error('Error al actualizar orden de favoritos:', error);
    throw error;
  }
};

/** Orden personalizado de tarjetas clima (por usuario). */
export interface ClimateRoomOrderItem {
  room_id: number;
  orden: number;
}

export const getClimateRoomOrder = async (userId: number): Promise<ClimateRoomOrderItem[]> => {
  const response = await api.get(`/usuarios/${userId}/climate-room-order`);
  return Array.isArray(response.data) ? response.data : [];
};

export const updateClimateRoomOrder = async (
  userId: number,
  orderData: ClimateRoomOrderItem[],
): Promise<void> => {
  await api.put(`/usuarios/${userId}/climate-room-order`, orderData);
};

export interface ClimateDeviceOrderItem {
  room_id: number;
  item_key: string;
  orden: number;
}

export const getClimateDeviceOrder = async (userId: number): Promise<ClimateDeviceOrderItem[]> => {
  const response = await api.get(`/usuarios/${userId}/climate-device-order`);
  return Array.isArray(response.data) ? response.data : [];
};

export const updateClimateDeviceOrder = async (
  userId: number,
  payload: { room_id: number; items: ClimateDeviceOrderItem[] },
): Promise<void> => {
  await api.put(`/usuarios/${userId}/climate-device-order`, payload);
};

export interface RoomDeviceOrderItem {
  room_id: number;
  item_key: string;
  orden: number;
}

export const getRoomDeviceOrder = async (
  userId: number,
  roomId: number,
): Promise<RoomDeviceOrderItem[]> => {
  const response = await api.get(`/usuarios/${userId}/room-device-order`, {
    params: { room_id: roomId },
  });
  return Array.isArray(response.data) ? response.data : [];
};

export const updateRoomDeviceOrder = async (
  userId: number,
  payload: { room_id: number; items: { item_key: string; orden: number }[] },
): Promise<void> => {
  await api.put(`/usuarios/${userId}/room-device-order`, payload);
};

export const getUserRoomSortPreference = async (
  roomId: number,
): Promise<{ sort_criterion: string; exists: boolean }> => {
  const response = await api.get(`/user-sort-preferences/${roomId}`);
  const data = response.data || {};
  return {
    sort_criterion: String(data.sort_criterion || 'personalizado'),
    exists: Boolean(data.exists),
  };
};

export const saveUserRoomSortPreference = async (
  roomId: number,
  sortCriterion: string,
): Promise<void> => {
  await api.post(`/user-sort-preferences/${roomId}`, { sort_criterion: sortCriterion });
};

// ============================================
// GRUPOS DE DISPOSITIVOS
// ============================================

// Interfaces TypeScript para Grupos
export interface GroupDevice {
  id: number;
  nombre: string;
  tipo: string;
  estado: string;
  consumo: number;
  online: boolean;
  habitacion_id: number | null;
  habitacion_nombre: string | null;
  tipo_artefacto: string | null;
  artefacto_id: number | null;
  mac: string;
  cover_position?: number | null;
}

export interface Group {
  id: number;
  name: string;
  device_type_category: 'relay' | 'cover' | 'pulsador';
  device_count: number;
  is_favorite?: boolean;
  created_at: string | null;
  updated_at: string | null;
  devices: GroupDevice[];
}

export interface CreateGroupData {
  name: string;
  device_type_category: 'relay' | 'cover' | 'pulsador';
  device_ids: number[];
}

export interface UpdateGroupData {
  name?: string;
  device_ids?: number[];
}

export interface GroupableDevices {
  relay: GroupDevice[];
  cover: GroupDevice[];
  pulsador: GroupDevice[];
}

// Función para obtener todos los grupos de un usuario (con caché en memoria)
export const getUserGroups = async (userId: number, forceRefresh: boolean = false): Promise<Group[]> => {
  try {
    const {
      isMapaGruposCargado,
      listGroupsFromMap,
      loadUserGroupsIntoMap,
    } = await import('./consumptionService');
    if (!forceRefresh && isMapaGruposCargado(userId)) {
      return listGroupsFromMap() as Group[];
    }
    return await loadUserGroupsIntoMap(userId, forceRefresh) as Group[];
  } catch (error) {
    console.error('Error al obtener grupos:', error);
    throw error;
  }
};

export const updateGroupsMap = async (userId: number, grupos: Group[]): Promise<void> => {
  const { upsertGroupsInMap } = await import('./consumptionService');
  upsertGroupsInMap(userId, grupos);
};

// Función para crear un nuevo grupo
export const createGroup = async (userId: number, groupData: CreateGroupData): Promise<any> => {
  try {
    const response = await api.post(`/usuarios/${userId}/grupos`, groupData);
    const nuevoGrupo = response.data;

    if (nuevoGrupo && nuevoGrupo.id && Array.isArray(nuevoGrupo.devices)) {
      await updateGroupsMap(userId, [nuevoGrupo]);
    } else if (nuevoGrupo?.group_id) {
      const details = await getGroupDetails(userId, nuevoGrupo.group_id, true);
      await updateGroupsMap(userId, [details]);
    }

    const { eventBus } = await import('./EventBus');
    eventBus.emit('grupos:actualizado');

    return nuevoGrupo;
  } catch (error: any) {
    try {
      const { loadUserGroupsIntoMap } = await import('./consumptionService');
      const todosLosGrupos = await loadUserGroupsIntoMap(userId, true);
      const grupoCreado = todosLosGrupos.find((g: any) => g.name === groupData.name);

      if (grupoCreado) {
        const { eventBus } = await import('./EventBus');
        eventBus.emit('grupos:actualizado');
        return grupoCreado;
      }
    } catch {
      // Si falla el refresh, continuar con el error original
    }

    throw error;
  }
};

// Función para obtener detalles de un grupo específico (con caché)
export const getGroupDetails = async (userId: number, groupId: number, forceRefresh: boolean = false): Promise<Group> => {
  try {
    const {
      isMapaGruposCargado,
      getGroupFromMap,
      upsertGroupsInMap,
    } = await import('./consumptionService');

    if (!forceRefresh) {
      const cached = getGroupFromMap(groupId);
      if (cached) return cached as Group;
      if (isMapaGruposCargado(userId)) {
        throw new Error('Grupo no encontrado');
      }
    }

    const response = await api.get(`/usuarios/${userId}/grupos/${groupId}`);
    const grupo = response.data;
    upsertGroupsInMap(userId, [grupo]);
    return getGroupFromMap(groupId) || grupo;
  } catch (error) {
    console.error('Error al obtener detalles del grupo:', error);
    throw error;
  }
};

// Función para actualizar un grupo existente
export const updateGroup = async (userId: number, groupId: number, updateData: UpdateGroupData): Promise<any> => {
  try {
    const response = await api.put(`/usuarios/${userId}/grupos/${groupId}`, updateData);
    const grupoActualizado = response.data;
    
    if (!grupoActualizado || !grupoActualizado.id) {
      throw new Error('Respuesta inválida del servidor: el grupo actualizado no tiene la estructura esperada');
    }
    
    if (!Array.isArray(grupoActualizado.devices)) {
      grupoActualizado.devices = [];
    }
    
    await updateGroupsMap(userId, [grupoActualizado]);
    
    const { eventBus } = await import('./EventBus');
    eventBus.emit('grupos:actualizado');
    
    return grupoActualizado;
  } catch (error) {
    console.error('Error al actualizar grupo:', error);
    throw error;
  }
};

// Función para eliminar un grupo
export const deleteGroup = async (userId: number, groupId: number): Promise<any> => {
  try {
    const response = await api.delete(`/usuarios/${userId}/grupos/${groupId}`);
    
    const { removeGroupFromMap } = await import('./consumptionService');
    removeGroupFromMap(groupId);
    
    const { eventBus } = await import('./EventBus');
    eventBus.emit('grupos:actualizado');
    
    return response.data;
  } catch (error) {
    console.error('Error al eliminar grupo:', error);
    throw error;
  }
};

// Función para obtener dispositivos agrupables por categoría
export const getGroupableDevices = async (userId: number): Promise<GroupableDevices> => {
  try {
    const response = await api.get(`/usuarios/${userId}/dispositivos-agrupables`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener dispositivos agrupables:', error);
    throw error;
  }
};

// Función para marcar/desmarcar grupo como favorito
export const toggleGroupFavorite = async (userId: number, groupId: number, isFavorite: boolean): Promise<any> => {
  try {
    const response = await api.put(`/usuarios/${userId}/grupos/${groupId}/favorito`, { is_favorite: isFavorite });
    
    const { getGroupFromMap, upsertGroupsInMap } = await import('./consumptionService');
    const grupo = getGroupFromMap(groupId);
    if (grupo) {
      grupo.is_favorite = isFavorite;
      upsertGroupsInMap(userId, [grupo]);
    }
    
    const { eventBus } = await import('./EventBus');
    eventBus.emit('favoritos:actualizado');
    eventBus.emit('grupos:actualizado');
    
    return response.data;
  } catch (error) {
    console.error('Error al actualizar favorito del grupo:', error);
    throw error;
  }
};

// Función para obtener grupos favoritos del usuario (con caché)
export const getUserFavoriteGroups = async (userId: number, forceRefresh: boolean = false): Promise<Group[]> => {
  try {
    const {
      isMapaGruposCargado,
      listFavoriteGroupsFromMap,
      loadUserGroupsIntoMap,
      mapaGruposFavoritos,
    } = await import('./consumptionService');

    if (!forceRefresh && isMapaGruposCargado(userId)) {
      return listFavoriteGroupsFromMap(userId) as Group[];
    }

    if (!forceRefresh) {
      const gruposFavoritos = Object.values(mapaGruposFavoritos[userId] || {});
      if (gruposFavoritos.length > 0) {
        return gruposFavoritos as Group[];
      }
    }

    try {
      const all = await loadUserGroupsIntoMap(userId, forceRefresh);
      return (all as Group[]).filter((g) => g.is_favorite);
    } catch {
      const response = await api.get(`/usuarios/${userId}/grupos-favoritos`);
      const gruposFavoritos = response.data || [];
      if (!mapaGruposFavoritos[userId]) {
        mapaGruposFavoritos[userId] = {};
      }
      gruposFavoritos.forEach((grupo: Group) => {
        mapaGruposFavoritos[userId][grupo.id] = grupo;
      });
      return gruposFavoritos;
    }
  } catch (error) {
    console.error('Error al obtener grupos favoritos:', error);
    throw error;
  }
};

// ============================================
// ESTADÍSTICAS FAVORITAS
// ============================================

export type PeriodOption = 
  | '1hour' | '6hours' | '12hours' | '24hours' 
  | '7days' | '30days' | '12months'
  | 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'thisYear'
  | 'billing' | 'custom';

export type IntervalOption = 'minute' | '5minutes' | '15minutes' | '30minutes' | 'hour' | 'day';

export interface StatisticsFavorite {
  id: number;
  user_id: number;
  title: string;
  period: PeriodOption;
  interval: IntervalOption;
  // displayMode eliminado - siempre graficamos potencia (W) y mostramos energía (Wh) en leyendas
  room_id: number | null; // null = dispositivos específicos, number = top 5 de habitación
  device_ids: number[]; // Array de IDs (máx 15 si room_id es null, top 5 si room_id existe)
  artifact_type: string | string[] | null; // Tipo(s) de artefacto seleccionado(s) (opcional, puede ser string o array)
  artifact_types?: string[]; // Array completo de tipos de artefactos (para compatibilidad)
  customStartDate?: string; // Fecha de inicio para período personalizado (opcional, no persistido en backend aún)
  customEndDate?: string; // Fecha de fin para período personalizado (opcional, no persistido en backend aún)
  orden: number;
  created_at: string;
  updated_at: string;
}

export interface CreateStatisticsFavoriteData {
  title: string;
  period: PeriodOption;
  interval: IntervalOption;
  // displayMode eliminado - siempre graficamos potencia (W) y mostramos energía (Wh) en leyendas
  room_id: number | null;
  device_ids: number[];
  artifact_type?: string | string[] | null; // Tipo(s) de artefacto seleccionado(s) (opcional, puede ser string o array)
}

export interface UpdateStatisticsFavoriteData {
  title?: string;
  period?: PeriodOption;
  interval?: IntervalOption;
  // displayMode eliminado - siempre graficamos potencia (W) y mostramos energía (Wh) en leyendas
  room_id?: number | null;
  device_ids?: number[];
  artifact_type?: string | string[] | null; // Tipo(s) de artefacto seleccionado(s) (opcional, puede ser string o array)
}

// Función para obtener estadísticas favoritas del usuario
export const getUserStatisticsFavorites = async (userId: number): Promise<StatisticsFavorite[]> => {
  try {
    const response = await api.get(`/usuarios/${userId}/estadisticas-favoritas`);
    return response.data || [];
  } catch (error) {
    console.error('Error al obtener estadísticas favoritas:', error);
    throw error;
  }
};

// Función para crear una estadística favorita
export const createStatisticsFavorite = async (userId: number, favoriteData: CreateStatisticsFavoriteData): Promise<StatisticsFavorite> => {
  try {
    // Debug: verificar qué se está enviando al backend
    console.log('[api.ts] createStatisticsFavorite - Datos a enviar:', {
      artifact_type: favoriteData.artifact_type,
      artifact_type_type: typeof favoriteData.artifact_type,
      artifact_type_isArray: Array.isArray(favoriteData.artifact_type),
      artifact_type_length: Array.isArray(favoriteData.artifact_type) ? favoriteData.artifact_type.length : 'N/A'
    });
    const response = await api.post(`/usuarios/${userId}/estadisticas-favoritas`, favoriteData);
    console.log('[api.ts] createStatisticsFavorite - Respuesta del backend:', {
      artifact_type: response.data.artifact_type,
      artifact_type_type: typeof response.data.artifact_type,
      artifact_type_isArray: Array.isArray(response.data.artifact_type)
    });
    return response.data;
  } catch (error) {
    console.error('Error al crear estadística favorita:', error);
    throw error;
  }
};

// Función para actualizar una estadística favorita
export const updateStatisticsFavorite = async (userId: number, favoriteId: number, updateData: UpdateStatisticsFavoriteData): Promise<StatisticsFavorite> => {
  try {
    const response = await api.put(`/usuarios/${userId}/estadisticas-favoritas/${favoriteId}`, updateData);
    return response.data;
  } catch (error: any) {
    console.error('[API] updateStatisticsFavorite ERROR:', error?.message || error, 'code:', error?.code, 'response:', error?.response?.data);
    throw error;
  }
};

// Función para eliminar una estadística favorita
export const deleteStatisticsFavorite = async (userId: number, favoriteId: number): Promise<any> => {
  try {
    const response = await api.delete(`/usuarios/${userId}/estadisticas-favoritas/${favoriteId}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar estadística favorita:', error);
    throw error;
  }
};

// Función para actualizar el orden de estadísticas favoritas
export const updateStatisticsFavoritesOrder = async (userId: number, orderData: { id: number; orden: number }[]): Promise<any> => {
  try {
    const response = await api.put(`/usuarios/${userId}/estadisticas-favoritas/orden`, orderData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar orden de estadísticas favoritas:', error);
    throw error;
  }
};

// ============================================
// CONSUMO HISTÓRICO - PICO MÁXIMO POR HABITACIÓN
// ============================================

// Función para obtener el pico máximo histórico de consumo por habitación (últimos 30 días)
export const getPeakConsumptionByRoom = async (): Promise<Array<{
  habitacion_id: number;
  habitacion_nombre: string;
  peak_consumption_w: number;
}>> => {
  try {
    console.log('[API] Llamando a /historical/consumption/peak-by-room');
    const response = await api.get('/historical/consumption/peak-by-room');
    console.log('[API] Respuesta recibida:', response.data);
    console.log('[API] Status:', response.status);
    
    if (response.data && response.data.success) {
      const data = response.data.data || [];
      console.log(`[API] Picos obtenidos: ${data.length} habitaciones`);
      if (data.length > 0) {
        console.log('[API] Primeros picos:', data.slice(0, 3));
      }
      return data;
    }
    
    console.error('[API] Error en la respuesta del servidor:', response.data);
    throw new Error(response.data?.error || 'Error en la respuesta del servidor');
  } catch (error: any) {
    console.error('[API] Error al obtener pico máximo de consumo por habitación:', error);
    if (error.response) {
      console.error('[API] Status del error:', error.response.status);
      console.error('[API] Detalles del error:', error.response.data);
    } else if (error.request) {
      console.error('[API] No se recibió respuesta del servidor:', error.request);
    } else {
      console.error('[API] Error al configurar la petición:', error.message);
    }
    // No lanzar el error, devolver array vacío para que no rompa la UI
    return [];
  }
};

// ============================================
// LÍMITES MANUALES DE GAUGES POR HABITACIÓN
// ============================================

// Función para guardar un límite manual de gauge para una habitación (solo admin)
export const saveGaugeManualLimit = async (
  roomId: number,
  limitKw: number
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await api.post('/gauge-manual-limits', {
      room_id: roomId,
      limit_kw: limitKw
    });
    return response.data;
  } catch (error: any) {
    console.error('Error al guardar límite manual de gauge:', error);
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Error al guardar límite manual'
    };
  }
};

// Función para obtener todos los límites manuales (globales, para todos los usuarios)
export const getGaugeManualLimits = async (): Promise<Array<{
  room_id: number;
  limit_kw: number;
}>> => {
  try {
    const response = await api.get('/gauge-manual-limits');
    return response.data || [];
  } catch (error: any) {
    console.error('Error al obtener límites manuales de gauge:', error);
    return [];
  }
};

// Función para eliminar un límite manual de gauge (solo admin)
export const deleteGaugeManualLimit = async (
  roomId: number
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await api.delete(`/gauge-manual-limits/${roomId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error al eliminar límite manual de gauge:', error);
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Error al eliminar límite manual'
    };
  }
};

// === Configuración runtime de logs (Propuesta 3) ===

export interface LogConfigState {
  enabled: boolean;
  level: string;
}

export interface LogConfigCatalogEntry {
  id: string;
  file: string;
  category: string;
  label_key: string;
  services: string[];
  protected: boolean;
  always_on?: boolean;
  current: LogConfigState;
}

export interface LogsCodeState {
  backend_code_enabled: boolean;
  frontend_code_enabled: boolean;
  updated_at?: string | null;
}

export interface LogsConfigResponse {
  success: boolean;
  catalog?: LogConfigCatalogEntry[];
  presets?: { id: string; level: string; label_key: string }[];
  readonly_logs?: { file: string; label_key: string }[];
  code_state?: LogsCodeState;
  error?: string;
}

export interface UpdateLogsConfigPayload {
  logs: Record<string, LogConfigState>;
  confirm_protected?: boolean;
}

export interface UpdateLogsConfigResponse {
  success: boolean;
  restarted?: string[];
  failed?: { service: string; error: string }[];
  changed_logs?: string[];
  message_key?: string;
  error?: string;
}

export const getLogsConfig = async (): Promise<LogsConfigResponse> => {
  const response = await api.get('/v1/logs/config', { timeout: 12000 });
  return response.data;
};

export const getLogsCodeState = async (): Promise<{ success: boolean; code_state?: LogsCodeState; error?: string }> => {
  const response = await api.get('/v1/logs/code-state', { timeout: 8000 });
  return response.data;
};

/** Capa 1: lee backend_code_enabled con fallback si /code-state no está disponible. */
export const fetchBackendCodeEnabled = async (): Promise<boolean | null> => {
  try {
    const codeData = await getLogsCodeState();
    if (codeData.success && codeData.code_state) {
      return codeData.code_state.backend_code_enabled;
    }
  } catch {
    // fallback abajo
  }
  try {
    const configData = await getLogsConfig();
    if (configData.success && configData.code_state) {
      return configData.code_state.backend_code_enabled;
    }
  } catch {
    // sin datos
  }
  return null;
};

export const updateLogsConfig = async (
  payload: UpdateLogsConfigPayload
): Promise<UpdateLogsConfigResponse> => {
  const response = await api.put('/v1/logs/config', payload);
  return response.data;
};

export interface UpdateLogsCodeStateResponse {
  success: boolean;
  code_state?: LogsCodeState;
  restarted?: string[];
  unchanged?: boolean;
  error?: string;
}

export const updateLogsCodeState = async (
  backend_code_enabled: boolean
): Promise<UpdateLogsCodeStateResponse> => {
  const response = await api.put('/v1/logs/code-state', { backend_code_enabled });
  return response.data;
};
